import React from "react";
import { useNavigate } from "react-router-dom";

interface ServiceFeature {
  title: string;
  description: string;
  icon: JSX.Element;
}

const WeldingServices: React.FC = () => {
  const navigate = useNavigate();

  const features: ServiceFeature[] = [
    {
      title: "Structural Welding",
      description:
        "Expert welding services for structural steel components, ensuring durability and compliance with industry standards.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
      ),
    },
    {
      title: "Pipe Welding",
      description:
        "Specialized pipe welding services for industrial applications, including pressure vessels and pipeline systems.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
          />
        </svg>
      ),
    },
    {
      title: "Custom Fabrication",
      description:
        "Tailored welding solutions for custom projects, from architectural elements to specialized equipment components.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z"
          />
        </svg>
      ),
    },
  ];

  // const processes = [
  //   {
  //     name: "MIG Welding",
  //     description:
  //       "Metal Inert Gas welding for high-quality, efficient metal joining.",
  //     image: "/mig-welding.jpg",
  //   },
  //   {
  //     name: "TIG Welding",
  //     description:
  //       "Tungsten Inert Gas welding for precise, clean welds on various metals.",
  //     image: "/tig-welding.jpg",
  //   },
  //   {
  //     name: "Arc Welding",
  //     description:
  //       "Traditional arc welding for robust, reliable metal connections.",
  //     image: "/arc-welding.jpg",
  //   },
  // ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-24">
        <div className="absolute inset-0">
          <img
            src="/about.jpg"
            alt="Welding Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black opacity-60"></div>
        </div>
        <div className="relative container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6">Welding Services</h1>
          <p className="text-lg text-gray-300 max-w-3xl">
            Professional welding services with a commitment to quality,
            precision, and safety. Our certified welders bring expertise to
            every project.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="text-orange-500 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Welding Processes */}
      {/* <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Our Welding Processes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {processes.map((process, index) => (
              <div
                key={index}
                className="bg-white rounded-lg overflow-hidden shadow-lg"
              >
                <img
                  src={process.image}
                  alt={process.name}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{process.name}</h3>
                  <p className="text-gray-600">{process.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Quality Assurance */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold mb-6">Quality Assurance</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-600 mb-4">
                  Our welding services meet the highest industry standards,
                  backed by:
                </p>
                <ul className="space-y-3">
                  {[
                    "Certified welders",
                    "Quality control inspections",
                    "Modern welding equipment",
                    "Safety compliance",
                    "Project documentation",
                  ].map((item, index) => (
                    <li key={index} className="flex items-center text-gray-600">
                      <svg
                        className="w-5 h-5 text-orange-500 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <img
                  src="/quality.jpg"
                  alt="Quality Assurance"
                  className="rounded-lg shadow-md"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Need Professional Welding Services?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your welding requirements. Our team is
            ready to deliver high-quality solutions for your projects.
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => navigate("/contacts")}
              className="bg-white text-orange-500 px-8 py-3 rounded hover:bg-gray-100 transition-colors"
            >
              Contact Us
            </button>
            <button
              onClick={() => navigate("/projects")}
              className="border-2 border-white text-white px-8 py-3 rounded hover:bg-white hover:text-orange-500 transition-colors"
            >
              View Projects
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WeldingServices;
